import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { PageTitle } from '@components/PageTitle';
import { queryKeys } from '@constants/queryKeys.constant';
import { ESmartEventTimelineKeyType } from '@enums/smartEvent.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { invalidateSmartEventTimeline } from '@helpers/queryCache/invalidateSmartEventTimeline.helper';
import { updateInterventionCounts } from '@helpers/queryCache/updateTabCounts.helper';
import {
  addDocumentsSchema,
  type AddDocumentsDto,
} from '@services/documents/dtos/add.dto';
import { interventionService } from '@services/interventions/service';
import type { GetAllInterventionDocumentsOutput } from '@services/interventions/types/getAllDocuments.type';

import { AddDocumentsForm } from '../../../@components/Documents/AddForm';

export const Route = createFileRoute(
  '/_layout/(private)/service-requests/$id/interventions/$interventionId/documents/add',
)({
  component: InterventionDocumentsAdd,
});

function InterventionDocumentsAdd() {
  const navigate = Route.useNavigate();
  const { id: serviceRequestId, interventionId } = Route.useParams();
  const queryClient = useQueryClient();

  const form = useForm<AddDocumentsDto>({
    resolver: zodResolver(addDocumentsSchema),
    defaultValues: {
      documents: [],
    },
  });

  const { isPending, mutate: addDocuments } = useMutation({
    mutationFn: interventionService.addDocuments,
    onError: handleRequestError,
    onSuccess: async ({ uploaded }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.interventions.documents(interventionId),
      });

      await queryClient.invalidateQueries({
        queryKey:
          queryKeys.serviceRequests.documentsWithInterventions(
            serviceRequestId,
          ),
      });

      queryClient.setQueriesData<GetAllInterventionDocumentsOutput>(
        { queryKey: queryKeys.interventions.documents(interventionId) },
        oldData => {
          if (!oldData) {
            return {
              list: uploaded,
            };
          }

          return {
            list: uploaded.concat(oldData.list),
          };
        },
      );

      updateInterventionCounts({
        queryClient,
        serviceRequestId,
        interventionId,
        field: 'documents',
        delta: uploaded.length,
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.interventions.detail(interventionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.interventions(serviceRequestId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.detail(serviceRequestId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.timeline(serviceRequestId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.all,
      });

      invalidateSmartEventTimeline({
        id: serviceRequestId,
        queryClient,
        type: ESmartEventTimelineKeyType.ServiceRequest,
      });

      toast.success('Success', {
        description: 'Document(s) added successfully',
      });
      navigate({
        to: '/service-requests/$id',
        params: { id: serviceRequestId },
        replace: true,
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    addDocuments({ documents: values.documents, id: interventionId });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle
        hasBackButton
        params={{ id: serviceRequestId }}
        title="Add documents"
        to="/service-requests/$id"
      />

      <AddDocumentsForm
        handleSubmit={handleSubmit}
        isLoading={isPending}
        serviceRequestId={serviceRequestId}
      />
    </Form.Provider>
  );
}
